import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Mail, Lock, User, ArrowLeft, Stethoscope, UserCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { toast } from 'sonner';
import { HealthCrossLogo } from '../components/HealthCrossLogo';

export const Auth = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { login, register, user } = useAuth();
  const { t } = useLanguage();
  
  const [mode, setMode] = useState(searchParams.get('mode') || 'login');
  const [role, setRole] = useState(searchParams.get('role') === 'medical' ? 'medical_professional' : 'patient');
  const [loading, setLoading] = useState(false);
  
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    name: ''
  });

  useEffect(() => {
    if (user) {
      if (user.role === 'admin') {
        navigate('/admin');
      } else {
        navigate(user.role === 'medical_professional' ? '/medical' : '/dashboard');
      }
    }
  }, [user, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      if (mode === 'login') {
        await login(formData.email, formData.password);
        toast.success(t('welcomeBack') + '!');
      } else {
        await register(formData.email, formData.password, formData.name, role);
        toast.success(t('createAccount') + '!');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Something went wrong');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-6">
      <div className="absolute inset-0 hero-glow pointer-events-none" />
      
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
        className="w-full max-w-md"
      >
        <Button
          variant="ghost"
          className="mb-6 rounded-full"
          onClick={() => navigate('/')}
          data-testid="back-btn"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          {t('back')}
        </Button>

        <Card className="p-8 rounded-3xl border-2 border-border">
          <div className="flex justify-center mb-6">
            <HealthCrossLogo className="w-16 h-16" iconClassName="w-8 h-8" />
          </div>

          <h1 className="text-2xl font-bold text-center mb-2">
            {mode === 'login' ? t('welcomeBack') : t('createAccount')}
          </h1>
          <p className="text-muted-foreground text-center mb-8">
            {mode === 'login' 
              ? t('signInToContinue')
              : t('startYourJourney')}
          </p>

          {mode === 'register' && (
            <div className="flex flex-wrap gap-2 mb-6">
              <Button
                type="button"
                variant={role === 'patient' ? 'default' : 'outline'}
                className="flex-1 rounded-full"
                onClick={() => setRole('patient')}
                data-testid="role-patient"
              >
                <UserCircle className="w-4 h-4 mr-2" />
                {t('patient')}
              </Button>
              <Button
                type="button"
                variant={role === 'medical_professional' ? 'default' : 'outline'}
                className="flex-1 rounded-full"
                onClick={() => setRole('medical_professional')}
                data-testid="role-medical"
              >
                <Stethoscope className="w-4 h-4 mr-2" />
                {t('medical')}
              </Button>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-5">
            {mode === 'register' && (
              <div className="space-y-2">
                <Label htmlFor="name" className="text-sm font-medium">{t('fullName')}</Label>
                <div className="relative">
                  <User className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    id="name"
                    type="text"
                    placeholder="João Silva"
                    className="pl-11 rounded-full"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    required
                    data-testid="input-name"
                  />
                </div>
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="email" className="text-sm font-medium">{t('email')}</Label>
              <div className="relative">
                <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  id="email"
                  type="email"
                  placeholder="voce@exemplo.com"
                  className="pl-11 rounded-full"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  required
                  data-testid="input-email"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="password" className="text-sm font-medium">{t('password')}</Label>
              <div className="relative">
                <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  id="password"
                  type="password"
                  placeholder="••••••••"
                  className="pl-11 rounded-full"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  required
                  minLength={6}
                  data-testid="input-password"
                />
              </div>
            </div>

            <Button
              type="submit"
              className="w-full rounded-full btn-scale"
              size="lg"
              disabled={loading}
              data-testid="submit-btn"
            >
              {loading ? t('loading') : mode === 'login' ? t('signIn') : t('createAccount')}
            </Button>
          </form>

          <div className="mt-6 text-center">
            <p className="text-muted-foreground text-sm">
              {mode === 'login' ? t('dontHaveAccount') : t('alreadyHaveAccount')}
              <Button
                variant="link"
                className="text-primary ml-1 p-0"
                onClick={() => setMode(mode === 'login' ? 'register' : 'login')}
                data-testid="toggle-mode"
              >
                {mode === 'login' ? t('signUp') : t('signIn')}
              </Button>
            </p>
          </div>
        </Card>
      </motion.div>
    </div>
  );
};
